// PendingAdditionsView.jsx - Head marker view of late uploads held for release.
// A pending addition is a page a student uploaded after their submission was
// already flagged/claimed/marked - it has no effect on the submission's real
// file list, order, or marking until released here (see releasePendingAdditions).
import React from 'react';
import { FilePlus, File } from 'lucide-react';
import { getAuthenticatedFileUrl } from '../firebase/submissions';

function PendingAdditionsView({
  pendingAdditions,
  homeworkNames,
  onRelease
}) {
  const openFile = async (filePath) => {
    try {
      const url = await getAuthenticatedFileUrl(filePath);
      if (url) {
        window.open(url, '_blank');
      } else {
        alert('Unable to access this file. Please contact support.');
      }
    } catch (error) {
      console.error('Error opening file:', error);
      alert('Error accessing file. Please try again.');
    }
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    }}>
      <h2 style={{ margin: '0 0 16px 0' }}>Pending Additions</h2>

      {pendingAdditions.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px',
          color: '#7f8c8d'
        }}>
          <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
            Nothing pending right now
          </div>
          <div>All caught up!</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {pendingAdditions.map(submission => (
            <div
              key={submission.id}
              style={{
                border: '2px solid #4a7fd6',
                borderRadius: '8px',
                padding: '20px',
                background: '#f0f5ff'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '20px'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#2c3e50',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    {submission.studentName}
                    {submission.cohortName && (
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#667eea',
                        background: '#eef2ff',
                        padding: '4px 12px',
                        borderRadius: '6px'
                      }}>
                        {submission.cohortName}
                      </span>
                    )}
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    fontSize: '14px',
                    color: '#7f8c8d',
                    marginBottom: '12px'
                  }}>
                    <span>{homeworkNames[submission.homeworkType] || submission.homeworkType}</span>
                    <span>Currently: {submission.status}</span>
                    {submission.pendingAdditionAt && (
                      <span>Uploaded {submission.pendingAdditionAt.toDate().toLocaleDateString()}</span>
                    )}
                    {submission.markerName && (
                      <span>| With: {submission.markerName}</span>
                    )}
                  </div>

                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#4a5568',
                    marginBottom: '6px'
                  }}>
                    Existing pages ({(submission.originalFilePaths || submission.originalFileUrls || []).length}) - unaffected by release:
                  </div>
                  {(submission.originalFilePaths || submission.originalFileUrls) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                      {(submission.originalFilePaths || submission.originalFileUrls).map((filePath, i) => (
                        <button
                          key={i}
                          onClick={() => openFile(filePath)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            background: '#f7fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '12px',
                            color: '#2d3748',
                            cursor: 'pointer'
                          }}
                        >
                          <File size={12} />
                          {(submission.fileNames && submission.fileNames[i]) || `File ${i + 1}`}
                        </button>
                      ))}
                    </div>
                  )}

                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#2c5aa0',
                    marginBottom: '6px'
                  }}>
                    New page(s) awaiting release ({(submission.pendingAdditionFilePaths || []).length}):
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {(submission.pendingAdditionFilePaths || []).map((filePath, i) => (
                      <button
                        key={i}
                        onClick={() => openFile(filePath)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          background: '#e0ebff',
                          border: '1px solid #4a7fd6',
                          borderRadius: '6px',
                          fontSize: '12px',
                          color: '#1a3d7c',
                          cursor: 'pointer'
                        }}
                      >
                        <FilePlus size={12} />
                        {(submission.pendingAdditionFileNames && submission.pendingAdditionFileNames[i]) || `New file ${i + 1}`}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '220px' }}>
                  <button
                    onClick={() => onRelease(submission.id)}
                    style={{
                      padding: '12px 24px',
                      background: '#4a7fd6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}
                    title="Add the new page(s) to the end of the file and send back to the marker for re-review - existing marks are unaffected"
                  >
                    Merge & Send to Marker
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PendingAdditionsView;
